import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
// import { Html5Qrcode } from 'html5-qrcode'
import ReactModal from 'react-modal'
import { CameraWrapper, HiddenInput, ToggleButton, Wrapper } from './Style'
import { useAlert, positions } from 'react-alert'

const qrcodeRegionId = 'html5qr-code-camera-region'

// Creates the configuration object for Html5QrcodeScanner.
const createConfig = (props: Pick<IDCardScannerProps, 'fps' | 'qrbox' | 'aspectRatio' | 'disableFlip'>) => {
  let config: {
    qrbox: number
    fps?: number
    aspectRatio?: number
    disableFlip?: boolean
  } = {
    qrbox: 248,
  }
  if (props.fps) {
    config.fps = props.fps
  }
  if (props.qrbox) {
    config.qrbox = props.qrbox
  }
  if (props.aspectRatio) {
    config.aspectRatio = props.aspectRatio
  }
  if (props.disableFlip !== undefined) {
    config.disableFlip = props.disableFlip
  }
  return config
}

const modalStyle = {
  overlay: {
    zIndex: 99,
    fontSize: 'calc(100vmin / 390 * 16)',
    backgroundColor: 'rgba(16, 4, 27, 0.5)',
  },
  content: {
    top: '50%',
    left: '50%',
    bottom: 'initial',
    right: 'initial',
    transform: 'translate(-50%, -50%)',
    padding: 0,
    border: 'initial',
  },
}

interface IDCardScannerProps {
  qrCodeSuccessCallback: (...args: any[]) => void
  qrCodeErrorCallback?: (...args: any[]) => void
  onClose?: () => void
  fps?: number
  qrbox?: number
  aspectRatio?: number
  disableFlip?: boolean
}

export default function IDCardScanner(props: IDCardScannerProps) {
  const [modalInitiated, setModalInitiated] = useState<boolean>()
  const [cameraStarted, setCameraStarted] = useState(false)
  const cameraWrapperEl = useRef<HTMLDivElement | null>(null)
  const alert = useAlert()

  const [qrCodeApp, setQrCodeApp] = useState<any>(null)

  const [qrCodeAppId, setQrCodeAppId] = useState(0)

  useEffect(() => {
    if (!modalInitiated) return

    let newQrCodeApp: any
    let aborted = false

    async function load() {
      const { Html5Qrcode } = await import('html5-qrcode')
      if (aborted) return
      newQrCodeApp = new Html5Qrcode(qrcodeRegionId)
      setQrCodeApp(newQrCodeApp)
    }
    load()

    return () => {
      aborted = true
      try {
        newQrCodeApp
          ?.stop()
          .then((ignore: any) => {
            // QR Code scanning is stopped.
          })
          .catch((err: any) => {
            // Stop failed, handle it.
          })
      } catch (err) {
        // ignore
      }
    }
  }, [modalInitiated, qrCodeAppId])

  // checking camera error
  useEffect(() => {
    if (!cameraStarted) {
      return
    }

    const MAX_ERROR_CHECK = 3
    let errorCount = 0

    const id = setInterval(() => {
      const el = cameraWrapperEl.current?.querySelector('video')

      if (el && !el.paused && el.currentTime === 0) {
        errorCount++
      } else {
        errorCount = 0
      }

      if (errorCount > MAX_ERROR_CHECK) {
        clearInterval(id)

        try {
          qrCodeApp
            ?.stop()
            .then(
              () => {
                setCameraStarted(false)
              },
              (err: any) => {
                setCameraStarted(false)
                // Stop failed, handle it.
                console.error(err)
                // we are unable to stop it normally, abort the instance
                setQrCodeAppId((i) => i + 1)
              }
            )
            .then(() => {
              alert.info('系统相机异常，请重启手机或是使用相片上传', {
                position: positions.MIDDLE,
                offset: '300px',
                timeout: 1500,
              })
            })
        } catch (err) {}
      }
    }, 500)

    return () => {
      clearInterval(id)
    }
  }, [alert, cameraStarted, qrCodeApp])

  const qrCodeHandler = useRef(props.qrCodeSuccessCallback)

  useLayoutEffect(() => {
    qrCodeHandler.current = props.qrCodeSuccessCallback
  })

  const onQrCodeSuccess = useCallback((...args: any[]) => {
    qrCodeHandler.current(...args)
  }, [])

  const qrCodeErrorHandler = useRef(props.qrCodeErrorCallback)

  useLayoutEffect(() => {
    qrCodeErrorHandler.current = props.qrCodeErrorCallback
  })
  const onQrCodeError = useCallback((...args: any[]) => {
    qrCodeErrorHandler.current?.(...args)
  }, [])

  const enableCamera = async () => {
    const config = createConfig(props)

    try {
      await qrCodeApp.stop()
    } catch (err) {}

    qrCodeApp.start({ facingMode: 'environment' }, config, onQrCodeSuccess).then(() => {
      setCameraStarted(true)
    })
  }

  const handleDropFile = async (ev: React.FormEvent<HTMLInputElement>) => {
    const file = ev.currentTarget.files?.[0]
    // FIXME: for some reason, scan will fail if qrcode is perfectly grid aligned on safari.
    // so we rotate it slightly and try again
    const scanWithRotation = async (file: File, degree: number = 0) => {
      if (degree === 0) {
        return qrCodeApp.scanFile(file, true)
      }

      const bitmap = await createImageBitmap(file)
      const canvas = document.createElement('canvas')
      canvas.width = bitmap.width
      canvas.height = bitmap.height
      const ctx2d = canvas.getContext('2d')
      if (!ctx2d) {
        throw new Error('2d context unavailable')
      }
      ctx2d.rotate((Math.PI / 180) * degree)
      ctx2d.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height, 0, 0, bitmap.width, bitmap.height)

      const pngBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob)
            return
          }
          reject(new Error('failed to build png blob'))
        }, 'png')
      })
      const pngFile = new File([pngBlob], 'test.png')
      return qrCodeApp.scanFile(pngFile, true)
    }
    if (file) {
      try {
        const t = await scanWithRotation(file, 0)
        onQrCodeSuccess(t)
        return
      } catch (err) {}
      try {
        const t = await scanWithRotation(file, 1)
        onQrCodeSuccess(t)
        return
      } catch (err) {}
      try {
        const t = await scanWithRotation(file, -1)
        onQrCodeSuccess(t)
        return
      } catch (err) {
        onQrCodeError(err)
      }
    }
  }

  const onCloseCamera = useCallback(() => {
    qrCodeApp
      ?.stop()
      .then((ignore: any) => {
        // QR Code scanning is stopped.
      })
      .catch((err: any) => {
        // Stop failed, handle it.
      })
      .then(() => {
        setCameraStarted(false)
      })
  }, [qrCodeApp])

  return (
    <ReactModal isOpen onAfterOpen={() => setModalInitiated(true)} style={modalStyle} onRequestClose={props.onClose}>
      <Wrapper>
        <CameraWrapper showPlaceholder detached={cameraStarted} onClick={enableCamera}>
          点击启用相机
        </CameraWrapper>
        <CameraWrapper ref={cameraWrapperEl} detached={!cameraStarted} id={qrcodeRegionId}></CameraWrapper>
        <ToggleButton hidden={!cameraStarted} onClick={onCloseCamera}>
          关闭相机
        </ToggleButton>
        <ToggleButton hidden={cameraStarted}>
          或是，上传相片
          <HiddenInput type="file" accept="image/*" onChange={handleDropFile} />
        </ToggleButton>
      </Wrapper>
      {/* <div id={qrcodeRegionId} /> */}
    </ReactModal>
  )
}
